import React, { useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'
import { useNavigate } from 'react-router-dom'
import { getThemeColors, THEME_OPTIONS } from '../theme'

const MODES = {
  LOGIN: 'login',
  REGISTER: 'register',
  FORGOT: 'forgot',
  RESET: 'reset',
}

const JOIN_WAITLIST_URL = 'https://fvnuxqgzkeghaimgqqcz.supabase.co/functions/v1/join-waitlist'

const buildStyles = (C) => ({
  page: {
    minHeight: '100vh',
    background: C.bg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'DM Sans', system-ui, sans-serif",
    padding: '2rem',
  },
  card: {
    background: C.white,
    border: `0.5px solid ${C.border}`,
    borderRadius: '16px',
    padding: '2.5rem',
    width: '100%',
    maxWidth: '400px',
    boxShadow: C.shadow,
  },
  logo: {
    fontSize: '15px',
    fontWeight: '500',
    color: C.text,
    marginBottom: '0.5rem',
  },
  logoAccent: { color: C.blue },
  title: {
    fontSize: '22px',
    fontWeight: '500',
    color: C.text,
    letterSpacing: '-0.5px',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '13px',
    color: C.subtle,
    marginBottom: '1.5rem',
  },
  tabs: {
    display: 'flex',
    gap: '4px',
    background: C.bg,
    borderRadius: '10px',
    padding: '4px',
    marginBottom: '1.5rem',
  },
  tab: (active) => ({
    flex: 1,
    padding: '8px',
    background: active ? C.blue : 'transparent',
    color: active ? '#fff' : '#6b7280',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    transition: 'all 0.2s',
  }),
  label: {
    display: 'block',
    fontSize: '12px',
    color: C.muted,
    marginBottom: '6px',
    marginTop: '1rem',
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    background: themeIsDark(C) ? '#0f172a' : C.bg,
    border: `0.5px solid ${C.border}`,
    borderRadius: '8px',
    color: C.text,
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  btn: {
    width: '100%',
    padding: '12px',
    background: C.blue,
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    marginTop: '1.5rem',
  },
  error: {
    background: C.redBg,
    border: `0.5px solid ${C.redBorder}`,
    color: C.red,
    padding: '10px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    marginTop: '1rem',
  },
  success: {
    background: C.greenBg,
    border: `0.5px solid ${C.greenBorder}`,
    color: C.green,
    padding: '10px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    marginTop: '1rem',
  },
  linkBtn: {
    background: 'none',
    border: 'none',
    color: C.blue,
    cursor: 'pointer',
    fontSize: '12px',
    marginTop: '0.75rem',
    padding: 0,
    textAlign: 'left',
  },
})

const themeIsDark = (C) => C.white !== '#fff'

export default function Login({ theme = THEME_OPTIONS.LIGHT }) {
  const C = getThemeColors(theme)
  const s = buildStyles(C)
  const [mode, setMode] = useState(MODES.LOGIN)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))
    const query = new URLSearchParams(window.location.search)
    const requestedMode = query.get('mode')
    const isRecovery = hash.get('type') === 'recovery' || requestedMode === 'recovery'

    if (isRecovery) {
      setMode(MODES.RESET)
      setError('')
      setSuccess('Tu enlace es valido. Elige una nueva contraseña para entrar a JobPipeline.')
      return
    }

    if (requestedMode === MODES.REGISTER) {
      setMode(MODES.REGISTER)
      return
    }

    if (requestedMode === MODES.FORGOT) {
      setMode(MODES.FORGOT)
    }
  }, [])

  const clearAlerts = () => {
    setError('')
    setSuccess('')
  }

  const upsertWaitlist = async (userId, emailValue) => {
    if (!userId || !emailValue) return
    await fetch(JOIN_WAITLIST_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        email: emailValue,
      }),
    })
  }

  const handleSubmit = async () => {
    setLoading(true)
    clearAlerts()

    try {
      if (mode === MODES.LOGIN) {
        const { error: loginError } = await supabase.auth.signInWithPassword({ email, password })

        if (loginError) {
          if (loginError.message === 'Email not confirmed') {
            setError('Confirma tu correo antes de iniciar sesion. Revisa tu bandeja de entrada y spam.')
          } else if (loginError.message === 'Invalid login credentials') {
            setError('Correo o contraseña incorrectos.')
          } else {
            setError(loginError.message)
          }
        } else {
          navigate('/dashboard')
        }
        return
      }

      if (mode === MODES.REGISTER) {
        const { data, error: signUpError } = await supabase.auth.signUp({ email, password })

        if (signUpError) {
          if (signUpError.message.includes('already registered') || signUpError.message.includes('already exists')) {
            setError('Ya existe una cuenta con este correo. Inicia sesion en la pestana "Iniciar sesion".')
          } else {
            setError(signUpError.message)
          }
        } else if (data?.user?.identities?.length === 0) {
          setError('Ya existe una cuenta con este correo. Inicia sesion en la pestana "Iniciar sesion".')
        } else if (data.session) {
          await upsertWaitlist(data.user.id, email)
          navigate('/onboarding')
        } else {
          await upsertWaitlist(data?.user?.id, email)
          setSuccess('Cuenta creada. Revisa tu correo y confirma tu acceso para activar JobPipeline.')
        }
        return
      }

      if (mode === MODES.FORGOT) {
        const redirectTo = `${window.location.origin}/login?mode=recovery`
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, { redirectTo })
        if (resetError) {
          if (resetError.message === 'email rate limit exceeded') {
            setError('Espera 1 o 2 minutos antes de solicitar otro enlace de recuperacion.')
            return
          }
          throw resetError
        }

        setSuccess('Si existe una cuenta confirmada para este correo, te enviaremos un enlace para restablecer tu contraseña. Revisa tu bandeja y spam.')
        return
      }

      if (!password || password.length < 8) {
        setError('Tu nueva contraseña debe tener al menos 8 caracteres.')
        return
      }

      if (password !== confirmPassword) {
        setError('Las contraseñas no coinciden.')
        return
      }

      const { error: updateError } = await supabase.auth.updateUser({ password })
      if (updateError) {
        if (updateError.message?.toLowerCase().includes('same password')) {
          setError('Elige una contraseña distinta a la anterior.')
          return
        }
        throw updateError
      }

      setSuccess('Tu contraseña se actualizo correctamente. Ya puedes entrar.')
      setMode(MODES.LOGIN)
      setPassword('')
      setConfirmPassword('')
      window.history.replaceState({}, '', '/login')
    } catch (err) {
      setError(err?.message || 'Ocurrio un error. Intenta de nuevo.')
    } finally {
      setLoading(false)
    }
  }

  const isLoginMode = mode === MODES.LOGIN
  const isRegisterMode = mode === MODES.REGISTER
  const isForgotMode = mode === MODES.FORGOT
  const isResetMode = mode === MODES.RESET

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logo}>Job<span style={s.logoAccent}>Pipeline</span></div>
        <div style={s.title}>
          {isLoginMode && 'Bienvenido de vuelta'}
          {isRegisterMode && 'Crear cuenta gratis'}
          {isForgotMode && 'Recuperar contraseña'}
          {isResetMode && 'Nueva contraseña'}
        </div>
        <div style={s.subtitle}>
          {isLoginMode && 'Accede a tu pipeline de vacantes'}
          {isRegisterMode && 'Empieza a buscar empleo con Narevya'}
          {isForgotMode && 'Te enviaremos un enlace para restablecer tu acceso'}
          {isResetMode && 'Guarda una contraseña nueva para volver a entrar'}
        </div>

        {!isForgotMode && !isResetMode && (
          <div style={s.tabs}>
            <button style={s.tab(isLoginMode)} onClick={() => { setMode(MODES.LOGIN); clearAlerts() }}>
              Iniciar sesion
            </button>
            <button style={s.tab(isRegisterMode)} onClick={() => { setMode(MODES.REGISTER); clearAlerts() }}>
              Registrarse
            </button>
          </div>
        )}

        {isRegisterMode && (
          <div style={{ fontSize: '12px', color: C.subtle, lineHeight: '1.7', marginTop: '1rem' }}>
            Al continuar, podras revisar y aceptar los <span onClick={() => navigate('/terminos?from=register')} style={{ color: C.blue, cursor: 'pointer' }}>Terminos y Condiciones</span> y el <span onClick={() => navigate('/privacidad?from=register')} style={{ color: C.blue, cursor: 'pointer' }}>Aviso de Privacidad</span> antes de entrar a la beta.
          </div>
        )}

        {!isResetMode && (
          <>
            <label style={s.label}>Correo electronico</label>
            <input
              style={s.input}
              type="email"
              placeholder="tu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </>
        )}

        {!isForgotMode && (
          <>
            <label style={s.label}>{isResetMode ? 'Nueva contraseña' : 'contraseña'}</label>
            <input
              style={s.input}
              type="password"
              placeholder={isResetMode ? 'Minimo 8 caracteres' : 'Tu contraseña'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            />
          </>
        )}

        {isResetMode && (
          <>
            <label style={s.label}>Confirmar nueva contraseña</label>
            <input
              style={s.input}
              type="password"
              placeholder="Repite tu nueva contraseña"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            />
          </>
        )}

        {error && <div style={s.error}>{error}</div>}
        {success && <div style={s.success}>{success}</div>}

        <button style={s.btn} onClick={handleSubmit} disabled={loading}>
          {loading && 'Cargando...'}
          {!loading && isLoginMode && 'Entrar'}
          {!loading && isRegisterMode && 'Crear cuenta gratis'}
          {!loading && isForgotMode && 'Enviar enlace'}
          {!loading && isResetMode && 'Guardar nueva contraseña'}
        </button>

        {isLoginMode && (
          <button style={s.linkBtn} onClick={() => { setMode(MODES.FORGOT); clearAlerts() }}>
            {'\u00BFOlvidaste tu contrase\u00F1a?'}
          </button>
        )}

        {(isForgotMode || isResetMode) && (
          <button style={s.linkBtn} onClick={() => { setMode(MODES.LOGIN); clearAlerts() }}>
            Volver a iniciar sesion
          </button>
        )}
      </div>
    </div>
  )
}




